import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Upload, AlertCircle, Download } from 'lucide-react'
import { studentService } from '@/lib/api/services/student-service'
import { useToast } from '@/components/ui/use-toast'
import { DEFAULT_AVATAR } from '@/constants'
import { useTranslation } from 'react-i18next'

interface StudentImportData {
  firstname: string
  lastname: string
  email: string
  cin: string
  password: string
  gender: string
  gradeLevel?: string
  phone?: string
  address?: string
  birthday?: Date
}

interface ImportResult {
  student: StudentImportData
  success: boolean
  error?: string
  rowNumber: number
}

interface BulkImportStats {
  total: number
  successful: number
  failed: number
  results: ImportResult[]
}

export function BulkStudentImport() {
  const { t } = useTranslation('admin')
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [importStats, setImportStats] = useState<BulkImportStats | null>(null)
  const [showFailedDetails, setShowFailedDetails] = useState(false)
  const { toast } = useToast()

  const parseCSV = (csvText: string): StudentImportData[] => {
    const lines = csvText.split('\n').filter(line => line.trim())
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim())
      const student: any = {}
      
      headers.forEach((header, index) => {
        const value = values[index] || ''
        switch (header) {
          case 'firstname':
          case 'first name':
            student.firstname = value
            break
          case 'lastname':
          case 'last name':
            student.lastname = value
            break
          case 'email':
            student.email = value
            break
          case 'cin':
            student.cin = value
            break
          case 'password':
            student.password = value
            break
          case 'gender':
            student.gender = value
            break
          case 'gradelevel':
          case 'grade level':
            student.gradeLevel = value
            break
          case 'phone':
            student.phone = value
            break
          case 'address':
            student.address = value
            break
          case 'birthday':
          case 'birthdate':
            if (value) {
              // Parse dd/mm/yyyy format
              const dateParts = value.split('/')
              if (dateParts.length === 3) {
                const day = parseInt(dateParts[0])
                const month = parseInt(dateParts[1]) - 1 // Month is 0-indexed in JS Date
                const year = parseInt(dateParts[2])
                student.birthday = new Date(year, month, day)
              }
            }
            break
        }
      })
      
      return student as StudentImportData
    })
  }

  const validateStudent = (student: StudentImportData): string | null => {
    if (!student.firstname || student.firstname.length < 2) {
      return t('student-folder.bulkImportStudent.validation.firstname.error')
    }
    if (!student.lastname || student.lastname.length < 2) {
      return t('student-folder.bulkImportStudent.validation.lastname.error')
    }
    if (!student.email || !/\S+@\S+\.\S+/.test(student.email)) {
      return t('student-folder.bulkImportStudent.validation.email.error')
    }
    if (!student.cin || student.cin.length !== 8) {
      return t('student-folder.bulkImportStudent.validation.cin.error')
    }
    if (!student.password || student.password.length < 6) {
      return t('student-folder.bulkImportStudent.validation.password.error')
    }
    if (!student.gender || !['male', 'female'].includes(student.gender.toLowerCase())) {
      return t('student-folder.bulkImportStudent.validation.gender.error')
    }
    return null
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile)
    } else {
      toast({
        title: t('student-folder.bulkImportStudent.fileUpload.invalidFile.title'),
        description: t('student-folder.bulkImportStudent.fileUpload.invalidFile.description'),
        variant: 'destructive'
      })
    }
  }

  const handleImport = async () => {
    if (!file) return

    setImporting(true)
    setProgress(0)
    setImportStats(null)

    try {
      const csvText = await file.text()
      const students = parseCSV(csvText)
      const results: ImportResult[] = []
      
      for (let i = 0; i < students.length; i++) {
        const student = students[i]
        const rowNumber = i + 2 // +2 because CSV starts at row 1 and we skip header
        
        // Validate student data
        const validationError = validateStudent(student)
        if (validationError) {
          results.push({
            student,
            success: false,
            error: validationError,
            rowNumber
          })
          setProgress(((i + 1) / students.length) * 100)
          continue
        }

        try {
          // Generate a default birthday (10 years old) if not provided
          const defaultBirthday = new Date()
          defaultBirthday.setFullYear(defaultBirthday.getFullYear() - 10)

          // Prepare student data for API
          const createData = {
            firstname: student.firstname,
            lastname: student.lastname,
            email: student.email,
            password: student.password,
            cin: student.cin,
            gender: student.gender.charAt(0).toUpperCase() + student.gender.slice(1).toLowerCase(),
            gradeLevel: student.gradeLevel || '',
            phone: student.phone || '',
            address: student.address || '',
            avatar: DEFAULT_AVATAR,
            birthday: student.birthday ? student.birthday.toISOString() : defaultBirthday.toISOString(),
            isActive: true,
            isPaid: true,
            role: 'Student',
            enrolledDate: new Date().toISOString()
          }

          await studentService.create({
            ...createData,
            role: "Student" as const
          })
          results.push({
            student,
            success: true,
            rowNumber
          })
        } catch (error: any) {
          let errorMessage = t('student-folder.bulkImportStudent.import.unknownError')
          
          // Better error handling for API responses
          if (error.response?.data) {
            const apiError = error.response.data
            
            // Log the full error for debugging
         // console.logFull API error:', JSON.stringify(apiError, null, 2))
            
            // Handle different error response formats
            if (typeof apiError.message === 'string') {
              errorMessage = apiError.message
            } else if (Array.isArray(apiError.message)) {
              // Handle array of error objects
              const errorMessages = apiError.message.map((err: any) => {
                if (typeof err === 'string') return err
                if (err.field && err.message) return `${err.field}: ${err.message}`
                return JSON.stringify(err)
              })
              errorMessage = errorMessages.join(', ')
            } else if (apiError.message && typeof apiError.message === 'object') {
              // Handle validation errors object
              const validationErrors = []
              if (apiError.message.email) {
                validationErrors.push(t('student-folder.bulkImportStudent.import.validation.emailExists'))
              }
              if (apiError.message.cin) {
                validationErrors.push(t('student-folder.bulkImportStudent.import.validation.cinExists'))
              }
              if (apiError.message.gradeLevel) {
                validationErrors.push(t('student-folder.bulkImportStudent.import.validation.invalidGradeLevel'))
              }
              if (apiError.message.class) {
                validationErrors.push(t('student-folder.bulkImportStudent.import.validation.invalidClass'))
              }
              errorMessage = validationErrors.length > 0 ? validationErrors.join(', ') : t('student-folder.bulkImportStudent.import.validation.error')
            } else if (apiError.error === 'CONFLICT') {
              errorMessage = t('student-folder.bulkImportStudent.import.conflictError')
            } else if (apiError.error === 'BAD_REQUEST') {
              errorMessage = t('student-folder.bulkImportStudent.import.invalidDataError')
            } else {
              errorMessage = apiError.error || t('student-folder.bulkImportStudent.import.serverError')
            }
          } else if (error.message) {
            errorMessage = error.message
          }
          
          results.push({
            student,
            success: false,
            error: errorMessage,
            rowNumber
          })
        }
        
        setProgress(((i + 1) / students.length) * 100)
      }

      const stats: BulkImportStats = {
        total: students.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length,
        results
      }

      setImportStats(stats)
      
      toast({
        title: t('student-folder.bulkImportStudent.notifications.success.title'),
        description: t('student-folder.bulkImportStudent.notifications.success.description', {
          successCount: stats.successful,
          totalCount: stats.total
        })
      })

    } catch (error) {
      toast({
        title: t('student-folder.bulkImportStudent.notifications.error.title'),
        description: t('student-folder.bulkImportStudent.notifications.error.description'),
        variant: 'destructive'
      })
    } finally {
      setImporting(false)
    }
  }

  const downloadTemplate = () => {
    const template = 'firstname,lastname,email,cin,password,gender,gradeLevel,phone,address,birthday\nJohn,Doe,john.doe@example.com,12345678,Password123!,Male,10th Grade,+1234567890,123 Main St,15/01/2010'
    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'student_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <Button onClick={() => setIsOpen(true)} className="gap-2">
        <Upload className="h-4 w-4" />
        {t('student-folder.bulkImportStudent.button')}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('student-folder.bulkImportStudent.modal.title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Template Download */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('student-folder.bulkImportStudent.modal.templateAlert.description')}
                <Button variant="link" onClick={downloadTemplate} className="p-0 ml-2">
                  <Download className="h-4 w-4 mr-1" />
                  {t('student-folder.bulkImportStudent.modal.templateAlert.downloadButton')}
                </Button>
              </AlertDescription>
            </Alert>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="csv-file">{t('student-folder.bulkImportStudent.modal.fileUpload.label')}</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={importing}
              />
            </div>

            {/* Import Progress */}
            {importing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('student-folder.bulkImportStudent.modal.importProgress.text')}</span>
                  <span>{t('student-folder.bulkImportStudent.modal.importProgress.percentage', { progress: Math.round(progress) })}</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {/* Import Results */}
            {importStats && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{importStats.total}</div>
                    <div className="text-sm text-blue-600">{t('student-folder.bulkImportStudent.modal.results.total.title')}</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{importStats.successful}</div>
                    <div className="text-sm text-green-600">{t('student-folder.bulkImportStudent.modal.results.successful.title')}</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{importStats.failed}</div>
                    <div className="text-sm text-red-600">{t('student-folder.bulkImportStudent.modal.results.failed.title')}</div>
                  </div>
                </div>

                {importStats.failed > 0 && (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowFailedDetails(!showFailedDetails)}
                    className="w-full"
                  >
                    {showFailedDetails 
                      ? t('student-folder.bulkImportStudent.modal.results.showDetails.hide') 
                      : t('student-folder.bulkImportStudent.modal.results.showDetails.show')}
                  </Button>
                )}

                {/* Failed Imports Details */}
                {showFailedDetails && importStats.failed > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">{t('student-folder.bulkImportStudent.modal.results.failedTable.title', { count: importStats.failed })}</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('student-folder.bulkImportStudent.modal.results.failedTable.headers.row')}</TableHead>
                          <TableHead>{t('student-folder.bulkImportStudent.modal.results.failedTable.headers.name')}</TableHead>
                          <TableHead>{t('student-folder.bulkImportStudent.modal.results.failedTable.headers.email')}</TableHead>
                          <TableHead>{t('student-folder.bulkImportStudent.modal.results.failedTable.headers.cin')}</TableHead>
                          <TableHead>{t('student-folder.bulkImportStudent.modal.results.failedTable.headers.error')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importStats.results
                          .filter(result => !result.success)
                          .map((result, index) => (
                            <TableRow key={index}>
                              <TableCell>{result.rowNumber}</TableCell>
                              <TableCell>
                                {result.student.firstname} {result.student.lastname}
                              </TableCell>
                              <TableCell>{result.student.email}</TableCell>
                              <TableCell>{result.student.cin}</TableCell>
                              <TableCell>
                                <Badge variant="destructive" className="text-xs">
                                  {result.error}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                {t('student-folder.bulkImportStudent.modal.buttons.close')}
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={!file || importing}
                className="gap-2"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    {t('student-folder.bulkImportStudent.modal.buttons.import.importing')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {t('student-folder.bulkImportStudent.modal.buttons.import.default')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
